// Which connected channel account an olap request reads. eBay datasets are scoped by the
// `Ebayaccountid` header (the integration service's account id — olap resolves the eBay
// user from it and answers 400 without it, 403 for another business profile's account).
//
// The header is sent from the eBay pages (`/ebay/*`) and the dev OLAP Explorer
// (`/dev/olap-explorer`), and only once an account is selected. Amazon pages, other
// `/dev/*` pages and the connect pages never carry it. The olap interceptor and the OLAP
// response cache key (createDataSourceV2) both go through `ebayAccountIdForPath`, so a
// cached answer is always keyed by the account it was fetched for.

export const EBAY_ACCOUNT_HEADER = 'Ebayaccountid';
export const EBAY_ROUTE_PREFIX = '/ebay';
// The explorer loads eBay and Shopify datasets on one route. Walmart and Meta stay on
// their own prefixes — this path is not added to those helpers.
const OLAP_EXPLORER_PATH = '/dev/olap-explorer';

export const isUnderPath = (path, prefix) => {
  const normalized = String(path || '').toLowerCase();
  return normalized === prefix || normalized.startsWith(`${prefix}/`);
};

const isChannelOrExplorerPath = (path, routePrefix) =>
  isUnderPath(path, routePrefix) || isUnderPath(path, OLAP_EXPLORER_PATH);

// The header value for this path + selection, or null when none is sent.
export const ebayAccountIdForPath = (path, accountId) =>
  isChannelOrExplorerPath(path, EBAY_ROUTE_PREFIX) && accountId != null ? String(accountId) : null;

// The current route's path. Read from the URL, as tokenService does: the interceptor runs
// outside components, and importing the router here would close an import cycle.
export const currentPath = () => (typeof window === 'undefined' ? '' : window.location.pathname);

const hasHeader = (headers, name) =>
  typeof headers?.has === 'function' ? headers.has(name) : headers?.[name] != null;

// Stamp the header onto an olap request config. An explicit per-request value wins.
export const stampEbayAccountHeader = (config, { path, accountId }) => {
  if (!config.headers || hasHeader(config.headers, EBAY_ACCOUNT_HEADER)) return config;
  const value = ebayAccountIdForPath(path, accountId);
  if (value) config.headers[EBAY_ACCOUNT_HEADER] = value;
  return config;
};

// Shopify datasets are scoped by the `Shopifylinkid` header: the integration service's
// id of the business profile's link to a store (olap maps it to the store server-side and
// answers 400 without it, 403 for a link that is not ACTIVE or belongs to another business
// profile). Same rules as eBay: the Shopify analytics pages (`/shopify/*`, never the
// `/integrations/shopify` connect pages) and the dev OLAP Explorer, and only once a link
// is selected. With no link selected the header is omitted.
export const SHOPIFY_LINK_HEADER = 'Shopifylinkid';
export const SHOPIFY_ROUTE_PREFIX = '/shopify';

export const shopifyLinkIdForPath = (path, linkId) =>
  isChannelOrExplorerPath(path, SHOPIFY_ROUTE_PREFIX) && linkId != null ? String(linkId) : null;

export const stampShopifyLinkHeader = (config, { path, linkId }) => {
  if (!config.headers || hasHeader(config.headers, SHOPIFY_LINK_HEADER)) return config;
  const value = shopifyLinkIdForPath(path, linkId);
  if (value) config.headers[SHOPIFY_LINK_HEADER] = value;
  return config;
};

// Walmart datasets are scoped by the `Walmartaccountid` header: the integration service's
// account id (olap resolves the seller from it server-side — the seller id never travels —
// and answers 400 without it, 403 for an account that is not ACTIVE or belongs to another
// business profile). Same rules as eBay: only on the Walmart analytics pages (`/walmart/*`,
// never the `/integrations/walmart` connections page) and only once an account is selected.
export const WALMART_ACCOUNT_HEADER = 'Walmartaccountid';
export const WALMART_ROUTE_PREFIX = '/walmart';

export const walmartAccountIdForPath = (path, accountId) =>
  isUnderPath(path, WALMART_ROUTE_PREFIX) && accountId != null ? String(accountId) : null;

export const stampWalmartAccountHeader = (config, { path, accountId }) => {
  if (!config.headers || hasHeader(config.headers, WALMART_ACCOUNT_HEADER)) return config;
  const value = walmartAccountIdForPath(path, accountId);
  if (value) config.headers[WALMART_ACCOUNT_HEADER] = value;
  return config;
};

// Meta Ads datasets are scoped by the `Metaadaccountid` header: the integration service's id of
// ONE connected ad account (`meta_integrated_resource.id`, the `id` of the resources API; olap
// resolves the ad account and its currency from it server-side, and answers 400 without it, 403
// for an ad account that is not connected to this business profile). Same rules as eBay: only on
// the Meta analytics pages (`/meta/*`, never the `/integrations/meta` connect pages) and only
// once an ad account is selected.
export const META_AD_ACCOUNT_HEADER = 'Metaadaccountid';
export const META_ROUTE_PREFIX = '/meta';

export const metaAdAccountIdForPath = (path, adAccountId) =>
  isUnderPath(path, META_ROUTE_PREFIX) && adAccountId != null ? String(adAccountId) : null;

export const stampMetaAdAccountHeader = (config, { path, adAccountId }) => {
  if (!config.headers || hasHeader(config.headers, META_AD_ACCOUNT_HEADER)) return config;
  const value = metaAdAccountIdForPath(path, adAccountId);
  if (value) config.headers[META_AD_ACCOUNT_HEADER] = value;
  return config;
};

// Whether the app mirrors the selected Amazon marketplace (`marketplace=` / `profileId=`) into
// the URL of this page. A Shopify, Walmart or Meta analytics page is scoped by its channel
// account, so it does not.
export const mirrorsMarketplaceInUrl = (path) =>
  !isUnderPath(path, SHOPIFY_ROUTE_PREFIX) &&
  !isUnderPath(path, WALMART_ROUTE_PREFIX) &&
  !isUnderPath(path, META_ROUTE_PREFIX);
